import { useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, Printer, Loader2, IdCard as IdCardIcon, Search } from "lucide-react";
import PageHero from "../components/PageHero";
import IdCardVisual from "../components/IdCardVisual";
import { api, formatApiErrorDetail } from "../lib/api";
import { fileToCompressedDataUrl } from "../lib/image";
import { DEPARTMENT_OPTIONS, BLOOD_GROUPS } from "../data/site";
import { useSite } from "../context/SiteContext";

// Navy + gold palette matching the uploaded ID card reference design.
const NAVY = "#0b2a63";
const GOLD = "#f2b01e";

const empty = {
  type: "student",
  name: "",
  father_name: "",
  mother_name: "",
  designation: "",
  department: "",
  sex: "",
  phone: "",
  email: "",
  dob: "",
  doj: "",
  semester: "",
  course: "",
  roll_no: "",
  session: "",
  abc_id: "",
  aadhar_no: "",
  permanent_address: "",
  district: "",
  pincode: "",
  photo: "",
  signature: "",
  blood_group: "",
  emergency_contact: "",
  identification_mark: "",
  address: "",
};

const SEMESTER_OPTS = ["Semester I", "Semester II", "Semester III", "Semester IV", "Semester V", "Semester VI", "Semester VII", "Semester VIII"];

const inputCls = "w-full bg-white border border-[#c6cddd] focus:border-[#f2b01e] outline-none px-4 py-2.5 rounded-sm text-sm text-[#1a2233] focus:ring-2 focus:ring-[#f2b01e]/30";
const labelCls = "block text-xs uppercase tracking-widest text-[#0b2a63]/70 mb-1.5";

export default function IdCard() {
  const { settings } = useSite();
  const p = settings.pages.idcard;
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [card, setCard] = useState(null);
  const [ack, setAck] = useState(null);
  const [lphone, setLphone] = useState("");
  const [checking, setChecking] = useState(false);
  const [lookupNote, setLookupNote] = useState(null);
  const photoRef = useRef();
  const signRef = useRef();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const employee = form.type === "employee";
  const [empUnlocked, setEmpUnlocked] = useState(false);
  const [pwModal, setPwModal] = useState(false);
  const [pw, setPw] = useState("");
  const EMP_PASSWORD = "KCCKCOEMP";

  const chooseType = (t) => {
    if (t === "employee" && !empUnlocked) { setPwModal(true); return; }
    set("type", t);
  };
  const submitPw = (e) => {
    e.preventDefault();
    if (pw === EMP_PASSWORD) {
      setEmpUnlocked(true);
      set("type", "employee");
      setPwModal(false);
      setPw("");
      toast.success("Employee section unlocked");
    } else {
      toast.error("Incorrect password");
    }
  };

  // Light neutral backdrop like the reference page (#e6e9f0).
  const sectionBg = "bg-[#e6e9f0]";

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      set("photo", await fileToCompressedDataUrl(file, { maxW: 450, quality: 0.7, mime: "image/jpeg" }));
    } catch {
      toast.error("Could not read photo");
    }
  };
  const onSign = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      set("signature", await fileToCompressedDataUrl(file, { maxW: 360, quality: 0.75, mime: "image/jpeg" }));
    } catch {
      toast.error("Could not read signature");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.photo || !form.signature) {
      toast.error("Please upload both photo and signature");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.createIdCard(form);
      setAck({ card_no: data.card_no, phone: form.phone });
      toast.success(`Request submitted: ${data.card_no}`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to generate card");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setCard(null);
    setAck(null);
    setForm(empty);
    if (photoRef.current) photoRef.current.value = "";
    if (signRef.current) signRef.current.value = "";
  };

  const checkStatus = async (e) => {
    e && e.preventDefault();
    setChecking(true); setCard(null); setLookupNote(null);
    try {
      const { data } = await api.lookupIdCard(lphone.trim());
      if (data.approved) {
        setCard(data);
        setTimeout(() => document.querySelector("[data-testid='idcard-lookup-result']")?.scrollIntoView({ behavior: "smooth" }), 150);
      } else if (data.rejected) {
        setLookupNote({ type: "rejected", text: data.remark || "Your ID card request was not approved. Please contact the college office." });
      } else {
        setLookupNote({ type: "pending", text: "Your card is pending approval by admin. Please check again later." });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No request found for this phone number");
    } finally { setChecking(false); }
  };

  const lookupNoteEl = lookupNote ? (
    <div data-testid="idcard-lookup-note" className={`mt-4 rounded-sm p-4 text-sm border ${lookupNote.type === "rejected" ? "bg-destructive/10 border-destructive/30 text-destructive" : "bg-amber-500/10 border-amber-500/30 text-amber-800"}`}>
      <p className="font-medium">{lookupNote.type === "rejected" ? "ID card request rejected" : "Pending approval"}</p>
      <p className="mt-1 text-[#1a2233]/80">{lookupNote.text}</p>
    </div>
  ) : null;

  if (ack) {
    return (
      <div data-testid="idcard-result-page">
        <PageHero label="Request Submitted" title="ID Card Request" subtitle="Your ID card request has been received and is pending approval." />
        <section className="bg-bone py-16 md:py-24">
          <div className="max-w-xl mx-auto px-6 text-center">
            <div data-testid="idcard-ack" className="bg-white border border-[#c6cddd] rounded-sm p-8">
              <h2 className="font-serif text-3xl" style={{ color: NAVY }}>Thank you!</h2>
              <p className="text-muted mt-3">Your request number is <span className="font-mono" style={{ color: NAVY }}>{ack.card_no}</span>.</p>
              <p className="text-muted mt-1">Use your <strong>phone number</strong> as the acknowledgement number to download your card after the admin approves it.</p>
              <p className="font-mono text-2xl mt-3 tracking-wider" style={{ color: NAVY }}>{ack.phone}</p>
            </div>

            <form onSubmit={checkStatus} className="mt-8 flex gap-2">
              <input data-testid="idcard-lookup-input" value={lphone} onChange={(e) => setLphone(e.target.value)} placeholder="Enter phone no. to check / download" required className="flex-1 bg-white border border-[#c6cddd] focus:border-[#f2b01e] outline-none px-4 py-3 rounded-sm" />
              <button data-testid="idcard-lookup-btn" type="submit" disabled={checking} className="text-white px-6 py-3 rounded-sm text-sm uppercase tracking-widest disabled:opacity-60" style={{ background: NAVY }}>Check</button>
            </form>
            {lookupNoteEl}
            <button onClick={reset} className="mt-4 text-xs uppercase tracking-widest text-muted hover:opacity-80" style={{ color: NAVY }}>Submit another request</button>
          </div>

          {card && (
            <div className="max-w-[1600px] mx-auto px-6 mt-12">
              <div className="print-area">
                <IdCardVisual data={card} college={settings.college} logo={settings.images && settings.images.logo} />
              </div>
              <div className="no-print flex justify-center mt-10">
                <button data-testid="print-card-btn" onClick={() => window.print()} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform" style={{ background: GOLD, color: NAVY }}>
                  <Printer size={16} /> Print / Download Card
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    );
  }

  return (
    <div data-testid="idcard-page">
      <PageHero label={p.label} title={p.title} subtitle={p.subtitle} />
      <section className={`${sectionBg} py-16 md:py-24 transition-colors duration-500`}>
        <div className="max-w-3xl mx-auto px-6">
          {/* Persistent lookup — lets returning students check / download an approved card
              without re-submitting the form (which used to create duplicate records). */}
          <div data-testid="idcard-lookup-card" className="bg-white/95 border border-[#c6cddd] rounded-sm p-5 md:p-6 shadow-xl mb-8">
            <div className="flex items-center gap-2" style={{ color: NAVY }}>
              <Search size={16} />
              <h3 className="font-serif text-lg">Already applied? Check / download your ID card</h3>
            </div>
            <p className="text-muted text-xs mt-1">Enter the phone number you registered with. Once admin approves your card, you can download it right here.</p>
            <form onSubmit={checkStatus} className="mt-4 flex gap-2">
              <input data-testid="idcard-lookup-input" value={lphone} onChange={(e) => setLphone(e.target.value)} placeholder="Enter your phone number" required className="flex-1 bg-white border border-[#c6cddd] focus:border-[#f2b01e] outline-none px-4 py-3 rounded-sm text-sm" />
              <button data-testid="idcard-lookup-btn" type="submit" disabled={checking} className="text-white px-6 py-3 rounded-sm text-sm uppercase tracking-widest disabled:opacity-60 inline-flex items-center gap-2" style={{ background: NAVY }}>
                {checking ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} Check
              </button>
            </form>
            {lookupNoteEl}
          </div>

          <form onSubmit={submit} data-testid="idcard-form" className="bg-white border border-[#c6cddd] rounded-sm p-6 md:p-10 shadow-2xl">
            {/* type toggle */}
            <div className="flex gap-3 mb-8">
              {["student", "employee"].map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`type-${t}`}
                  onClick={() => chooseType(t)}
                  className={`flex-1 py-3 rounded-sm text-sm uppercase tracking-widest border transition-colors ${form.type === t ? "text-white" : "bg-white hover:border-[#0b2a63]"}`}
                  style={form.type === t ? { background: NAVY, borderColor: NAVY } : { color: NAVY, borderColor: "#c6cddd" }}
                >
                  <IdCardIcon size={15} className="inline mr-2" /> {t}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className={labelCls}>Full Name *</label>
                <input data-testid="field-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} required />
              </div>

              <div>
                <label className={labelCls}>Father's Name *</label>
                <input data-testid="field-father" className={inputCls} value={form.father_name} onChange={(e) => set("father_name", e.target.value)} required />
              </div>
              <div>
                <label className={labelCls}>Mother's Name</label>
                <input data-testid="field-mother" className={inputCls} placeholder="Optional" value={form.mother_name} onChange={(e) => set("mother_name", e.target.value)} />
              </div>

              {employee && (
                <div className="md:col-span-2">
                  <label className={labelCls}>Designation *</label>
                  <input data-testid="field-designation" className={inputCls} value={form.designation} onChange={(e) => set("designation", e.target.value)} required />
                </div>
              )}

              {!employee && (
                <>
                  <div>
                    <label className={labelCls}>Semester *</label>
                    <select data-testid="field-semester" className={inputCls} value={form.semester} onChange={(e) => set("semester", e.target.value)} required>
                      <option value="">Select semester</option>
                      {SEMESTER_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>ABC ID *</label>
                    <input data-testid="field-abc" className={inputCls} value={form.abc_id} onChange={(e) => set("abc_id", e.target.value)} required />
                  </div>
                  <div>
                    <label className={labelCls}>Course</label>
                    <input data-testid="field-course" className={inputCls} placeholder="e.g. B.A." value={form.course} onChange={(e) => set("course", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>Roll No.</label>
                    <input data-testid="field-roll-no" className={inputCls} placeholder="Optional" value={form.roll_no} onChange={(e) => set("roll_no", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>Session</label>
                    <input data-testid="field-session" className={inputCls} placeholder="e.g. 2026 – 2029" value={form.session} onChange={(e) => set("session", e.target.value)} />
                  </div>
                </>
              )}

              {employee && (
                <>
                  <div>
                    <label className={labelCls}>Date of Joining *</label>
                    <input data-testid="field-doj" className={inputCls} placeholder="DD/MM/YYYY" value={form.doj} onChange={(e) => set("doj", e.target.value)} required />
                  </div>
                  <div>
                    <label className={labelCls}>Aadhar No. *</label>
                    <input data-testid="field-aadhar" className={inputCls} value={form.aadhar_no} onChange={(e) => set("aadhar_no", e.target.value)} required />
                  </div>
                </>
              )}

              <div>
                <label className={labelCls}>Department *</label>
                <select data-testid="field-department" className={inputCls} value={form.department} onChange={(e) => set("department", e.target.value)} required>
                  <option value="">Select department</option>
                  {DEPARTMENT_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Sex *</label>
                <select data-testid="field-sex" className={inputCls} value={form.sex} onChange={(e) => set("sex", e.target.value)} required>
                  <option value="">Select</option>
                  <option>Male</option>
                  <option>Female</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Phone No. *</label>
                <input data-testid="field-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} required />
              </div>
              <div>
                <label className={labelCls}>Email Address</label>
                <input type="email" data-testid="field-email" className={inputCls} placeholder="Optional — get your card emailed on approval" value={form.email} onChange={(e) => set("email", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Date of Birth *</label>
                <input data-testid="field-dob" className={inputCls} placeholder="DD/MM/YYYY" value={form.dob} onChange={(e) => set("dob", e.target.value)} required />
              </div>
              <div>
                <label className={labelCls}>Blood Group *</label>
                <select data-testid="field-blood" className={inputCls} value={form.blood_group} onChange={(e) => set("blood_group", e.target.value)} required>
                  <option value="">Select</option>
                  {BLOOD_GROUPS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Emergency Contact No. *</label>
                <input data-testid="field-emergency" className={inputCls} value={form.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} required />
              </div>
              <div>
                <label className={labelCls}>Identification Mark *</label>
                <input data-testid="field-idmark" className={inputCls} value={form.identification_mark} onChange={(e) => set("identification_mark", e.target.value)} required />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Present Address *</label>
                <input data-testid="field-address" className={inputCls} value={form.address} onChange={(e) => set("address", e.target.value)} required />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Permanent Address *</label>
                <input data-testid="field-permanent" className={inputCls} value={form.permanent_address} onChange={(e) => set("permanent_address", e.target.value)} required />
              </div>
              <div>
                <label className={labelCls}>District *</label>
                <input data-testid="field-district" className={inputCls} value={form.district} onChange={(e) => set("district", e.target.value)} required />
              </div>
              <div>
                <label className={labelCls}>Pincode *</label>
                <input data-testid="field-pincode" className={inputCls} value={form.pincode} onChange={(e) => set("pincode", e.target.value)} required />
              </div>

              {/* uploads */}
              <div>
                <label className={labelCls}>Photo *</label>
                <label className="flex items-center gap-3 border border-dashed border-[#0b2a63]/30 rounded-sm px-4 py-3 cursor-pointer hover:border-[#f2b01e] transition-colors">
                  <Upload size={16} className="text-[#0b2a63]/60" />
                  <span className="text-sm text-muted truncate">{form.photo ? "Photo selected" : "Upload photo"}</span>
                  <input ref={photoRef} data-testid="upload-photo" type="file" accept="image/*" onChange={onPhoto} className="hidden" />
                </label>
                {form.photo && <img src={form.photo} alt="preview" className="mt-3 w-20 h-24 object-cover rounded border border-[#c6cddd]" />}
              </div>
              <div>
                <label className={labelCls}>Signature *</label>
                <label className="flex items-center gap-3 border border-dashed border-[#0b2a63]/30 rounded-sm px-4 py-3 cursor-pointer hover:border-[#f2b01e] transition-colors">
                  <Upload size={16} className="text-[#0b2a63]/60" />
                  <span className="text-sm text-muted truncate">{form.signature ? "Signature selected" : "Upload signature"}</span>
                  <input ref={signRef} data-testid="upload-signature" type="file" accept="image/*" onChange={onSign} className="hidden" />
                </label>
                {form.signature && <img src={form.signature} alt="sign preview" className="mt-3 h-12 object-contain" />}
              </div>
            </div>

            <button data-testid="generate-card-btn" type="submit" disabled={busy} className="mt-8 w-full text-white py-4 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform disabled:opacity-60 inline-flex items-center justify-center gap-2" style={{ background: NAVY }}>
              {busy ? <><Loader2 size={16} className="animate-spin" /> Generating…</> : <>Generate ID Card</>}
            </button>
          </form>
        </div>
      </section>

      {card && (
        <section className="bg-bone py-12 md:py-16" data-testid="idcard-lookup-result">
          <div className="max-w-[1600px] mx-auto px-6">
            <div className="print-area">
              <IdCardVisual data={card} college={settings.college} logo={settings.images && settings.images.logo} />
            </div>
            <div className="no-print flex justify-center mt-10">
              <button data-testid="print-card-btn" onClick={() => window.print()} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform" style={{ background: GOLD, color: NAVY }}>
                <Printer size={16} /> Print / Download Card
              </button>
            </div>
          </div>
        </section>
      )}

      {pwModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setPwModal(false)}>
          <form data-testid="emp-password-form" onClick={(e) => e.stopPropagation()} onSubmit={submitPw} className="bg-white w-full max-w-sm rounded-sm border border-[#c6cddd] p-8">
            <h3 className="font-serif text-2xl" style={{ color: NAVY }}>Employee Section</h3>
            <p className="text-muted text-sm mt-2 mb-5">Enter the employee access password to register an employee ID card.</p>
            <input data-testid="emp-password-input" type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" className={inputCls} />
            <div className="flex gap-3 mt-6">
              <button type="button" onClick={() => { setPwModal(false); setPw(""); }} className="flex-1 border border-[#c6cddd] py-3 rounded-full text-sm uppercase tracking-widest hover:bg-[#e6e9f0] transition-colors" style={{ color: NAVY }}>Cancel</button>
              <button data-testid="emp-password-submit" type="submit" className="flex-1 text-white py-3 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform" style={{ background: NAVY }}>Unlock</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
